import {
  arithmeticOperationsAddSub,
  arithmeticOperationsMulDiv
} from '@utils/fimUtils'
import {
  BarElement,
  CategoryScale,
  ChartConfiguration,
  ChartData,
  Chart as ChartJS,
  Legend,
  LegendElement,
  LinearScale,
  Title,
  Tooltip,
  TooltipItem
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { LabelOptions } from 'chartjs-plugin-datalabels/types/options'
import { useCallback, useEffect, useState } from 'react'
import { Bar } from 'react-chartjs-2'
import { VIFReportData } from 'src/pages/app/AssessmentReport/AssessmentReportVIFTable'

import { useLocation, useNavigate } from '@suntex.core/nav'
import { $$$ } from '@suntex.core/text'
import { withMatching } from '@suntex.core/text/extensions'
import { Database_Procs_VIFAggregatedByLevel_Item } from '@suntex.fim/api-client'
import { $ArithmeticOperation } from '@suntex.fim/enums'
import { useAppQueryString } from '@suntex.fim/nav'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
)

const colors = {
  '64 Facts': '#9376A7',
  '100 Facts': '#E16D6D',
  '10k Facts': '#F9BF34',
  '36 Facts': '#83E2B8'
}

export const levelKeyMap = {
  '64 Facts': {
    Addition: 'vif_add',
    Subtraction: 'vif_sub'
  },
  '100 Facts': {
    Addition: 'vif_add_rules_dd',
    Subtraction: 'vif_sub_rules_dd',
    Multiplication: 'vif_mul',
    Division: 'vif_div'
  },
  '10k Facts': {
    Addition: 'vif_add_1',
    Subtraction: 'vif_sub_2',
    Multiplication: 'vif_mul_1',
    Division: 'vif_div_1'
  },
  '36 Facts': {
    Multiplication: 'vif_mul_rules_dd_2',
    Division: 'vif_div_rules_dd'
  }
}

// Helper functions for URL parameter handling

/**
 * Converts hiddenDatasets query parameter string to Set
 * @param hiddenDatasets - Comma-separated string of dataset names
 * @returns Set of dataset names that should be hidden in the chart
 */
const parseHiddenDatasets = (hiddenDatasets: string | null): Set<string> => {
  return hiddenDatasets
    ? new Set(hiddenDatasets.split(',').filter(Boolean))
    : new Set()
}

/**
 * Creates base chart configuration options shared across all VIF chart instances
 * Includes legend setup, data labels, tooltips, scales, and interaction settings
 * @param onLegendClick - Optional callback function triggered when legend items are clicked
 *                        Used to handle dataset visibility toggling and URL updates
 * @returns Chart.js configuration options object for bar charts
 */
const createBaseChartOptions = (
  onLegendClick?: (datasetLabel: string, isVisible: boolean) => void
): ChartConfiguration<'bar'>['options'] => ({
  responsive: true,
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        // usePointStyle: true,
        boxWidth: 10,
        boxHeight: 10
      },
      ...(onLegendClick && {
        onClick: function (
          this: LegendElement<'bar'>,
          _event,
          legendItem,
          legend
        ) {
          // legendItem: LegendItem type, which may have datasetIndex as number | undefined
          const chart = legend.chart as ChartJS
          const datasetIndex = legendItem.datasetIndex
          if (typeof datasetIndex !== 'number') return
          const datasetLabel = chart.data.datasets[datasetIndex]?.label

          // Toggle visibility
          const isVisible = chart.isDatasetVisible(datasetIndex)
          chart.setDatasetVisibility(datasetIndex, !isVisible)
          chart.update()

          // Call the callback to update URL
          if (typeof datasetLabel === 'string') {
            onLegendClick(datasetLabel, !isVisible)
          }
        }
      })
    },
    datalabels: {
      skipNull: true,
      color: 'black',
      formatter: (value: string) => {
        return value !== undefined ? `${value}%\n` : '' // patch:bottom margin not applicable so added with lines
      },
      font: {
        size: 14,
        weight: 700
      },
      anchor: 'end'
    } as LabelOptions,
    tooltip: {
      filter: (context) => {
        const labelText = $$$(context.label, withMatching())
        const datasetText = $$$(context.dataset.label, withMatching())
        return (
          (labelText.matchesAny(['Addition', 'Subtraction']) &&
            datasetText.matchesAny(['64 Facts', '100 Facts', '10k Facts'])) ||
          (labelText.matchesAny(['Multiplication', 'Division']) &&
            datasetText.matchesAny(['36 Facts', '100 Facts', '10k Facts']))
        )
      },
      borderColor: '#D2D2D2',
      borderWidth: 1,
      displayColors: false,
      bodyAlign: 'center',
      backgroundColor: 'white',
      titleColor: 'black',
      bodyColor: 'black',
      bodyFont: {
        size: 14,
        weight: 700
      }
    }
  },
  maintainAspectRatio: false,
  layout: { autoPadding: true, padding: { top: 20 } },
  scales: {
    x: {
      ticks: {
        color: 'black',
        font: {
          size: 14,
          weight: 700
        }
      }
    }
  },
  skipNull: true,
  interaction: {
    intersect: false,
    mode: 'nearest',
    axis: 'x'
  }
})

/**
 * Creates customized chart options by extending base options with custom tooltip callbacks
 * Allows for context-specific tooltip formatting while maintaining consistent base styling
 * @param tooltipCallbacks - Optional custom tooltip formatting functions
 *                          - label: formats individual data point tooltips
 *                          - title: formats tooltip header/title
 * @param onLegendClick - Optional callback for legend click handling
 * @returns Complete Chart.js configuration with merged base options and custom tooltips
 */
const createChartOptions = (
  tooltipCallbacks?: {
    label?: (context: TooltipItem<'bar'>) => string
    title?: (context: TooltipItem<'bar'>[]) => string
  },
  onLegendClick?: (datasetLabel: string, isVisible: boolean) => void
): ChartConfiguration<'bar'>['options'] => {
  const baseOptions = createBaseChartOptions(onLegendClick)
  return {
    ...baseOptions,
    plugins: {
      ...baseOptions?.plugins,
      tooltip: {
        ...baseOptions?.plugins?.tooltip,
        callbacks: tooltipCallbacks || {
          label: (context: TooltipItem<'bar'>) => {
            const label = context.parsed.y?.toString() || ''
            return ` ${label} %`
          },
          title: (context: TooltipItem<'bar'>[]) => {
            const label = context[0]?.dataset.label || ''
            return label
          }
        }
      }
    }
  }
}

// Default options for backward compatibility
export const options: ChartConfiguration<'bar'>['options'] =
  createChartOptions()

export function getLabels(level: number) {
  let labels: string[]
  if (level === 0) {
    labels = $ArithmeticOperation
      .describeAll(arithmeticOperationsAddSub())
      .map((op) => op.label)
  } else if (level === 1) {
    labels = $ArithmeticOperation
      .describeAll(arithmeticOperationsMulDiv())
      .map((op) => op.label)
  } else {
    labels = $ArithmeticOperation.describeAll().map((op) => op.label)
  }
  return labels
}

/**
 * returns JSX for assessment very important facts chart
 */
function AssessmentVIFCharts({
  level,
  chartData,
  students = 1
}: {
  students: number
  level: number
  chartData: VIFReportData
}) {
  const { hiddenDatasets: hiddenDatasetsQuery } = useAppQueryString()
  const navigate = useNavigate()
  const location = useLocation()

  // State to track hidden datasets
  const [hiddenDatasets, setHiddenDatasets] = useState<Set<string>>(() => {
    return parseHiddenDatasets(hiddenDatasetsQuery)
  })

  /**
   * Handles legend item clicks to toggle dataset visibility
   * Updates both component state and URL to persist the visibility changes
   * @param datasetLabel - Name of the dataset being toggled (e.g., '64 Facts', '100 Facts')
   * @param isVisible - Current visibility state of the dataset after toggle
   */
  const handleLegendClick = useCallback(
    (datasetLabel: string, isVisible: boolean) => {
      const newHiddenDatasets = new Set(hiddenDatasets)
      if (isVisible) {
        newHiddenDatasets.delete(datasetLabel)
      } else {
        newHiddenDatasets.add(datasetLabel)
      }
      setHiddenDatasets(newHiddenDatasets)

      // Update URL with current query parameters from useAppQueryString
      const params = new URLSearchParams(location.search)
      if (newHiddenDatasets.size > 0) {
        params.set('hiddenDatasets', Array.from(newHiddenDatasets).join(','))
      } else {
        params.delete('hiddenDatasets')
      }
      const newSearch = params.toString()
      const newURL = newSearch
        ? `${location.pathname}?${newSearch}`
        : location.pathname
      navigate(newURL, { replace: true })
    },
    [hiddenDatasets, navigate, location]
  )

  // Update state when URL changes (e.g., browser back/forward)
  useEffect(() => {
    const handlePopState = () => {
      setHiddenDatasets(parseHiddenDatasets(hiddenDatasetsQuery))
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [hiddenDatasetsQuery])

  let dataSetkeys = ['64 Facts', '100 Facts', '10k Facts']

  const labels = getLabels(level)
  if (level === 0) {
    dataSetkeys = ['64 Facts', '100 Facts', '10k Facts']
  } else if (level === 1) {
    dataSetkeys = ['36 Facts', '100 Facts', '10k Facts']
  } else {
    dataSetkeys = ['64 Facts', '36 Facts', '100 Facts', '10k Facts']
  }

  const PERCENTAGE_DIVISOR = 100
  const barChartOptions = createChartOptions(
    {
      label: (context: TooltipItem<'bar'>) => {
        const label = context.parsed.y || ''
        const outOfStudents =
          students > 1
            ? Math.round((students * Number(label)) / PERCENTAGE_DIVISOR)
            : Number(label)
        const message =
          outOfStudents > 0
            ? `${outOfStudents} of ${students} (${label}%) students mastered this category.`
            : 'No students.'
        return ` ${message}`
      },
      title: (context: TooltipItem<'bar'>[]) => {
        const label = context[0]?.dataset.label || ''
        return label
      }
    },
    handleLegendClick
  )

  const data: ChartData<'bar', Array<number | undefined>, string> = {
    labels,
    datasets: dataSetkeys.map((key) => ({
      label: key,
      data: labels.map((label: any) => {
        const value = Number(
          chartData[
            `${levelKeyMap[key][label]}_completed_count` as keyof Database_Procs_VIFAggregatedByLevel_Item
          ]
        )
        if (Number.isNaN(value)) {
          return undefined
        }
        const PERCENTAGE_MULTIPLIER = 100
        const DECIMAL_PLACE = 1
        return (((Number(value) / students) * PERCENTAGE_MULTIPLIER).toFixed(
          DECIMAL_PLACE
        ) || 0) as number
      }),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      backgroundColor: colors[key],
      borderRadius: 6,
      borderSkipped: false,
      // Set hidden state based on URL parameter
      hidden: hiddenDatasets.has(key)
    }))
  }
  return <Bar options={barChartOptions} data={data} />
}

export default AssessmentVIFCharts
